import 'css/src/components/common/public-pages/PublicPageHeader';

import React, { useRef } from 'react';
import Immutable from 'immutable';
import classNames from 'classnames';

import { Urls } from 'js/src/client';
import {
    BUTTON_COLORS,
    InlineLink,
    PrimaryButton,
    SecondaryButton,
} from 'js/src/components/common/ui/Button';
import { Dropdown } from 'js/src/components/common/ui/Dropdown';
import { ImmutableFromJS } from 'js/src/libs/consts';

const UserDropdownToggleProps = ImmutableFromJS([
    {
        text: 'Dashboard',
        id: 'header-dashboard',
        href: Urls.DASHBOARD,
    },
    {
        text: 'Account settings',
        id: 'header-account-settings',
        href: Urls.ACCOUNT,
    },
    {
        text: 'Logout',
        id: 'header-logout',
        href: Urls.LOGOUT,
    },
]);
type Props = {
    backgroundColor?: string;
    invertTextColor?: boolean;
};

const PublicPageHeader = React.memo(function PublicPageHeader({
    backgroundColor = undefined,
    invertTextColor = false,
}: Props) {
    const navBarRef = useRef<HTMLElement | null>(null);

    const style: Record<string, string> = {};
    if (backgroundColor) {
        style['backgroundColor'] = backgroundColor;
    }

    let textColorClass = 'color-grey-800 fw-semibold';
    let linkTextColorClass = ''; // Defaults to the button's text color
    let logoImageSrc =
        'https://storage.googleapis.com/lumen5-site-images/L5-logo/2024/Lumen5-PrimaryLogo.svg';
    let navbarShadeClass = 'navbar-light';

    if (invertTextColor) {
        textColorClass = 'color-grey-200';
        linkTextColorClass = textColorClass;
        logoImageSrc =
            'https://storage.googleapis.com/lumen5-site-images/lumen5-logo/Lumen5-Single-ReverseColor.webp';
        navbarShadeClass = 'navbar-dark';
    }

    // Hardcoded Features dropdown links
    const featuresLinks = [
        {
            text: 'Blog to Video',
            url: 'https://lumen5.com/features/blog-to-video',
        },
        {
            text: 'Talking Head Videos',
            url: 'https://lumen5.com/features/talking-head',
        },
        {
            text: 'AI Voiceover Generator',
            url: 'https://lumen5.com/features/ai-voiceover-video-generator',
        },
        {
            text: 'AI Script Writing',
            url: 'https://lumen5.com/features/ai-script-writing',
        },
        {
            text: 'Video Clip Maker',
            url: 'https://lumen5.com/features/video-clip-maker',
        },
        {
            text: 'Automatic Translation',
            url: 'https://lumen5.com/features/automatic-video-translation',
        },
        {
            text: 'Turn Ideas to Videos',
            url: 'https://lumen5.com/features/idea-to-video',
        },
        {
            text: 'Animated Captions',
            url: 'https://lumen5.com/features/animated-video-captions',
        },
        {
            text: 'In-App Recording',
            url: 'https://lumen5.com/features/in-app-recording',
        },
        {
            text: 'Branding Customizations',
            url: 'https://lumen5.com/features/video-branding-customization',
        },
        {
            text: 'AI Video Blueprints',
            url: 'https://lumen5.com/features/video-blueprints',
        },
        {
            text: 'Video Collaboration',
            url: 'https://lumen5.com/features/collaborate-review-approve',
        },
        {
            text: 'Media Library',
            url: 'https://lumen5.com/features/media-library',
        },
        {
            text: 'Video Templates',
            url: 'https://lumen5.com/features/video-templates-scene-designs',
        },
    ];

    const createNode = (
        <li className="nav-item dropdown me-l5-lg-8">
            <a
                className={`nav-link text-decoration-none font-l5 font-l5--regular ${textColorClass} text-nowrap`}
                href="#"
                id="create-dropdown"
                data-bs-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
            >
                Features
                <i className="material-symbols-outlined align-center expand_more">expand_more</i>
            </a>
            <div
                className="dropdown-menu"
                aria-labelledby="create-dropdown"
                style={{ columnCount: 2 }}
            >
                {featuresLinks.map((link, index) => {
                    return (
                        <a key={index} className={'dropdown-item font-l5'} href={link.url}>
                            {link.text}
                        </a>
                    );
                })}
            </div>
        </li>
    );

    const userDropdownToggleProps = React.useMemo(() => {
        return Immutable.fromJS({
            text: SERVER.USER?.first_name || SERVER.USER?.email,
            color: BUTTON_COLORS.SIMPLE,
            iconLeft: (
                <i
                    className={classNames(
                        'material-symbols-outlined rounded-circle border me-l5-4',
                        textColorClass,
                    )}
                >
                    person
                </i>
            ),
            iconRight: (
                <i
                    className={classNames(
                        'material-symbols-outlined align-center ms-l5-4 expand_more',
                        textColorClass,
                    )}
                >
                    expand_more
                </i>
            ),
            textClassName: textColorClass,
        });
    }, [textColorClass]);

    let userOptionNode = null;
    if (SERVER.USER) {
        userOptionNode = (
            <Dropdown
                toggleProps={userDropdownToggleProps}
                itemPropsList={UserDropdownToggleProps}
            />
        );
    } else {
        userOptionNode = (
            <>
                <div className="d-none d-lg-block">
                    <InlineLink
                        href="https://lumen5.com/book-a-demo?utm_campaign=6880892-%5BMARKETING%5D%20Demo%20Request%20Form&utm_source=Website&utm_medium=header-menu"
                        text={
                            <span className={linkTextColorClass}>
                                {'Request' + '\xa0' + 'a' + '\xa0' + 'demo'}
                            </span>
                        }
                        className={'h-100'}
                    />
                </div>
                <div className="d-none d-lg-block">
                    <span
                        className={classNames(
                            'mx-l5-16 d-block align-content-center h-100',
                            linkTextColorClass,
                        )}
                    >
                        |
                    </span>
                </div>
                <div className="d-none d-lg-block">
                    <InlineLink
                        data-testid="header-signin-button"
                        href={Urls.LOGIN}
                        text={<span className={linkTextColorClass}>Log in</span>}
                        className={classNames(
                            'h-100 me-l5-lg-24 font-color-override',
                            linkTextColorClass,
                        )}
                    />
                </div>
                <div className="mb-l5-12 d-md-block d-lg-none border-bottom">
                    <a
                        className={`nav-link text-decoration-none ${textColorClass} font-l5 font-l5--regular`}
                        href="https://lumen5.com/book-a-demo?utm_campaign=6880892-%5BMARKETING%5D%20Demo%20Request%20Form&utm_source=Website&utm_medium=header-menu"
                    >
                        Request a demo
                    </a>
                </div>
                <div className="d-block">
                    <PrimaryButton
                        data-testid="header-signup-button"
                        href={Urls.SIGNUP}
                        text={'Sign' + '\xa0' + 'up'}
                    />
                </div>
                <div className="mt-l5-8 mb-l5-16 d-md-block d-lg-none d-block">
                    <SecondaryButton href={Urls.LOGIN} text="Log in" />
                </div>
            </>
        );
    }

    return (
        <>
            <nav
                className={`navbar navbar-expand-lg fixed-top ${navbarShadeClass} header-container py-l5-24 px-l5-48`}
                style={style}
                ref={navBarRef}
            >
                <div className="d-flex ps-l5-lg-24 col-2 align-items-center">
                    <a href={Urls.HOME} className="navbar-brand p-l5-0 d-flex align-items-center">
                        <img
                            className="rounded float-start nav-bar__logo"
                            src={logoImageSrc}
                            alt="Lumen5 Logo"
                            loading="eager"
                            // @ts-expect-error React 18 has a bug not recognizing fetchPriority: https://github.com/facebook/react/issues/28948
                            // eslint-disable-next-line react/no-unknown-property
                            fetchpriority="high"
                        />
                    </a>
                </div>
                <button
                    className="navbar-toggler border-0 me-l5-24"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#collapsedBarId"
                    aria-controls="collapsedBarId"
                    aria-expanded="false"
                    aria-label="Toggle navigation"
                >
                    <span className="navbar-toggler-icon"></span>
                </button>

                <div className="collapse navbar-collapse align-items-center" id="collapsedBarId">
                    <div className="d-flex align-items-center">
                        <ul className="navbar-nav align-items-center">
                            <li className="nav-item me-l5-lg-8">
                                <a
                                    className={`nav-link text-decoration-none ${textColorClass} font-l5 font-l5--regular`}
                                    href={Urls.PRICING}
                                >
                                    Pricing
                                </a>
                            </li>
                            {createNode}
                            <li className="nav-item me-l5-lg-8 dropdown">
                                <a
                                    className={`nav-link text-nowrap text-decoration-none font-l5 font-l5--regular ${textColorClass}`}
                                    href="#"
                                    id="enterprise-dropdown"
                                    data-bs-toggle="dropdown"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                >
                                    Enterprise
                                    <i className="material-symbols-outlined align-center expand_more">
                                        expand_more
                                    </i>
                                </a>
                                <div
                                    className="dropdown-menu"
                                    aria-labelledby="enterprise-dropdown"
                                >
                                    <a className="dropdown-item" href="/enterprise">
                                        Lumen5 for Enterprise
                                    </a>
                                    <a
                                        className="dropdown-item"
                                        href="https://content.lumen5.com/case-studies"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Case Studies
                                    </a>
                                    <a
                                        className="dropdown-item"
                                        href="https://lumen5.com/use-cases"
                                    >
                                        Use Cases
                                    </a>
                                    <a
                                        className="dropdown-item"
                                        href="https://fast.wistia.com/embed/channel/d92lo5wcg8"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Branded Templates
                                    </a>
                                    <a
                                        className="dropdown-item"
                                        href="https://ps.lumen5.com"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Professional Services
                                    </a>
                                    <a
                                        className="dropdown-item"
                                        href="https://lumen5.com/integrations"
                                    >
                                        Integrations
                                    </a>
                                    <a
                                        className="dropdown-item"
                                        href="https://lumen5.com/book-a-demo"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Request a demo
                                    </a>
                                </div>
                            </li>
                            <li className="nav-item me-l5-lg-8 dropdown">
                                <a
                                    className={`nav-link text-nowrap text-decoration-none font-l5 font-l5--regular ${textColorClass}`}
                                    href="#"
                                    id="solutions-dropdown"
                                    data-bs-toggle="dropdown"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                >
                                    Solutions
                                    <i className="material-symbols-outlined align-center expand_more">
                                        expand_more
                                    </i>
                                </a>
                                <div className="dropdown-menu" aria-labelledby="solutions-dropdown">
                                    <a
                                        className="dropdown-item"
                                        href="https://lumen5.com/solutions/professional-services"
                                    >
                                        Professional Service Firms
                                    </a>
                                    <a
                                        className="dropdown-item"
                                        href="https://lumen5.com/solutions/tech-telecom"
                                    >
                                        Technology & Telecom
                                    </a>
                                    <a
                                        className="dropdown-item"
                                        href="https://lumen5.com/solutions/manufacturing"
                                    >
                                        Manufacturing
                                    </a>
                                    <a
                                        className="dropdown-item"
                                        href="https://lumen5.com/solutions/banking-financial"
                                    >
                                        Banking & Financial
                                    </a>
                                    <a
                                        className="dropdown-item"
                                        href="https://lumen5.com/solutions/oil-gas-mining"
                                    >
                                        Oil, Gas & Mining
                                    </a>
                                    <a
                                        className="dropdown-item"
                                        href="https://lumen5.com/solutions/media-publishing"
                                    >
                                        Media, Publishing, Entertainment
                                    </a>
                                    <a
                                        className="dropdown-item"
                                        href="https://lumen5.com/solutions/government-healthcare-nonprofit"
                                    >
                                        Government, Healthcare, Nonprofit
                                    </a>
                                    <a
                                        className="dropdown-item"
                                        href="https://lumen5.com/solutions/education-university-college"
                                    >
                                        Education, Universities, Colleges
                                    </a>
                                    <a
                                        className="dropdown-item"
                                        href="https://lumen5.com/solutions/ai-learning-development"
                                    >
                                        Learning & Development
                                    </a>
                                    <a
                                        className="dropdown-item"
                                        href="https://lumen5.com/solutions/internal-comms-video-platform"
                                    >
                                        Internal Communications
                                    </a>
                                </div>
                            </li>
                            <li className="nav-item me-l5-lg-8 dropdown">
                                <a
                                    className={`nav-link text-nowrap text-decoration-none font-l5 font-l5--regular ${textColorClass}`}
                                    href="#"
                                    id="learn-dropdown"
                                    data-bs-toggle="dropdown"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                >
                                    Learn
                                    <i className="material-symbols-outlined align-center expand_more">
                                        expand_more
                                    </i>
                                </a>
                                <div className="dropdown-menu" aria-labelledby="learn-dropdown">
                                    <a className="dropdown-item" href="/learn/">
                                        Blog
                                    </a>
                                    <a
                                        className="dropdown-item"
                                        href="https://pod.co/supercharge-marketing"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Podcast
                                    </a>
                                    <a className="dropdown-item" href="/learn/resources">
                                        Resources
                                    </a>
                                </div>
                            </li>
                            <li className="nav-item mb-l5-12 d-block d-lg-none border-bottom">
                                <a
                                    className={`nav-link text-decoration-none ${textColorClass} font-l5 font-l5--regular`}
                                    href="https://lumen5.com/book-a-demo?utm_campaign=6880892-%5BMARKETING%5D%20Demo%20Request%20Form&utm_source=Website&utm_medium=header-menu"
                                >
                                    Request a demo
                                </a>
                            </li>
                            <li className="nav-item d-block d-lg-none">
                                <PrimaryButton href={Urls.SIGNUP} text={'Sign' + '\xa0' + 'up'} />
                            </li>
                            <li className="nav-item mt-l5-8 mb-l5-16 d-block d-lg-none">
                                <SecondaryButton href={Urls.LOGIN} text="Log in" />
                            </li>
                        </ul>
                    </div>
                </div>
                <div className="justify-content-end d-none d-lg-block">
                    <div className="d-flex h-100 align-items-center">{userOptionNode}</div>
                </div>
                <div className="header-gradient-border" />
            </nav>
        </>
    );
});

export default PublicPageHeader;
